import { useLocalStorage } from "@vueuse/core";
import { defineStore, skipHydrate } from "pinia";
import { computed, ref, watch } from "vue";
import type { Profile } from "@/server-types";
import * as http from "@/tools/http";
import * as ws from "@/tools/websocket";

type ExtendedProfile = Profile & { sentFriendRequests: string[]; receivedFriendRequests: string[] };

export const useAuthStore = defineStore("auth", () => {
	const accessToken = useLocalStorage("access_token", "");
	const refreshToken = useLocalStorage("refresh_token", "");

	const profile = ref<ExtendedProfile>();
	const initializing = ref(true);
	const googleCode = ref("");

	const hasValidTokens = computed(() => {
		return (
			refreshToken.value.length > 0 &&
			refreshTokenInfo.value !== undefined &&
			typeof refreshTokenInfo.value.exp === "number" &&
			new Date(refreshTokenInfo.value.exp * 1000) > new Date() &&
			// access token
			accessToken.value.length > 0 &&
			accessTokenInfo.value !== undefined &&
			typeof accessTokenInfo.value.exp === "number" &&
			new Date(accessTokenInfo.value.exp * 1000) > new Date()
		);
	});

	const loggedIn = computed(() => hasValidTokens.value && !!profile.value);

	const isAdmin = computed(() => {
		return accessTokenInfo.value && accessTokenInfo.value.admin;
	});

	const accessTokenInfo = computed(() => {
		try {
			if (accessToken.value == undefined) return undefined;
			return JSON.parse(atob(accessToken.value.split(".")[1]));
		} catch (e) {
			return undefined;
		}
	});

	const refreshTokenInfo = computed(() => {
		try {
			if (refreshToken.value == undefined) return undefined;
			return JSON.parse(atob(refreshToken.value.split(".")[1]));
		} catch (e) {
			return undefined;
		}
	});

	const username = computed(() => {
		return accessTokenInfo.value?.username;
	});

	async function refresh() {
		profile.value = (await http.getProfile(accessTokenInfo.value!.username)) as ExtendedProfile;
	}

	async function handleLoginChanged(loggedIn: boolean) {
		console.log("[AUTH] LOGIN CHANGED ->", loggedIn);
		if (loggedIn) {
			try {
				const prof = await http.getProfile(accessTokenInfo.value!.username);
				profile.value = prof;
				ws.initSocket();
			} catch {
				profile.value = undefined;
				accessToken.value = "";
				ws.closeSocket();
			}
		} else {
			accessToken.value = "";
			ws.closeSocket();
			profile.value = undefined;
		}
	}

	function setTokens(refresh: string, access: string) {
		// track
		localStorage.setItem("refresh_backup_" + new Date().getTime(), refresh);
		refreshToken.value = refresh;
		accessToken.value = access;
	}

	function logout() {
		console.log("[AUTH] LOGOUT");
		localStorage.setItem("last_logout_time", String(new Date().getTime()));
		refreshToken.value = "";
		accessToken.value = "";
	}

	async function refreshAccessTokenIfNeeded() {
		if (refreshToken.value == undefined || refreshTokenInfo.value == undefined) {
			console.warn("no refresh token");
			return;
		}

		const now = Date.now();
		const refreshExpDate = new Date(refreshTokenInfo.value.exp * 1000);

		if (now > refreshExpDate.getTime()) {
			// refresh has expired, we have to login again
			console.log("refresh has expired, we have to login again");
			localStorage.setItem("refresh_expired_ts", String(new Date().getTime()));
			refreshToken.value = "";
			return;
		}

		if (accessTokenInfo.value == undefined) {
			console.warn("no saved access token, refreshing");
			return await _refreshAccessToken();
		}

		const accessExpDate = new Date(accessTokenInfo.value.exp * 1000);
		const accessIatDate = new Date(accessTokenInfo.value.iat * 1000);

		const middle = new Date((accessExpDate.getTime() - accessIatDate.getTime()) / 2 + accessIatDate.getTime());

		if (now > middle.getTime()) {
			console.warn("access token passed middle, refreshing");
			return await _refreshAccessToken();
		}
	}

	async function _refreshAccessToken() {
		try {
			const resp = await http.refreshAccessToken(refreshToken.value);
			if (typeof resp.access_token === "string") {
				accessToken.value = resp.access_token;
			}
		} catch (error) {
			console.error("cannot refresh token", error);
		}
	}

	watch(
		hasValidTokens,
		(loggedIn) => {
			if (!initializing.value) void handleLoginChanged(loggedIn);
		},
	);

	// initial call
	const ready = refreshAccessTokenIfNeeded()
		.then(() => handleLoginChanged(hasValidTokens.value))
		.finally(() => { initializing.value = false; });

	// refreshAccessTokenIfNeeded().then(() => {
	//     ready.value = true;
	//     handleLoginChanged(loggedIn.value);
	// });

	// check access token once every 6 hours
	setInterval(
		() => {
			refreshAccessTokenIfNeeded();
		},
		1000 * 60 * 60 * 6 /* 6 hours in ms */
	);

	return {
		initializing,
		ready,
		accessToken: skipHydrate(accessToken),
		accessTokenInfo,
		refreshTokenInfo,
		username,
		loggedIn,
		profile,
		refresh,
		googleCode,
		setTokens,
		logout,
		isAdmin,
		handleLoginChanged
	};
});
